/**
 * Put every tool on a set of workflow steps back to un-applied.
 *
 * Used by the backward revert. Restoring the target step's session snapshot
 * already rolls most tool stores back to how they stood at that step, but it is
 * the wrong thing to rely on alone: a step reconstructed by the replay engine has
 * no session record at all, and a tool whose store the snapshot does not cover
 * keeps its applied flag forever. Either way the card ends up claiming work that
 * is no longer in the socket — and since the revert also deletes that tool's undo
 * material, its Undo button is left pointing at nothing.
 *
 * The per-tool reset logic already exists in `toolRegistry.resetApplied` (it is
 * what the snapshot-fallback undo path uses), so this reuses it rather than
 * duplicating a second set of "un-apply this tool" rules that could drift.
 */
import { toolRegistry } from '../components/tool/toolRegistry';
import { toolIdsForStep } from '../../features/step-checkpoints/undo/undoManifestStore';

const LOG_PREFIX = '[StepToolReset]';

/**
 * @param {string[]} stepIds — workflow steps whose tools should return to un-applied.
 */
export function resetAppliedToolsForSteps(stepIds) {
  for (const stepId of stepIds ?? []) {
    // The step's *recorded* tool list, not this build's: a tool reassigned to
    // another step since this design was worked on still has an applied card here
    // and must still be reset. See `toolIdsForStep`.
    for (const toolId of toolIdsForStep(stepId)) {
      try {
        toolRegistry[toolId]?.resetApplied?.();
      } catch (e) {
        // One tool's reset must not abandon the rest of the revert.
        console.warn(`${LOG_PREFIX} ${stepId}/${toolId} reset failed`, e);
      }
    }
  }
}

export default resetAppliedToolsForSteps;
